import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calculator, CheckCircle, Info, RefreshCw } from 'lucide-react';

export const IncomeTaxCalculatorPage = () => {
  const [formData, setFormData] = useState({
    totalIncome: '',
    age: 'below60',
    taxRegime: 'new',
    deduction80C: '',
    deduction80D: '',
    homeLoanInterest: '',
    nps: '',
  });

  const [result, setResult] = useState(null);

  const calculateOldRegimeTax = (income, age) => {
    let taxableIncome = income;
    
    // Apply deductions for old regime
    const total80C = Math.min(parseFloat(formData.deduction80C) || 0, 150000);
    const total80D = Math.min(parseFloat(formData.deduction80D) || 0, age === 'above60' ? 50000 : 25000);
    const homeLoan = Math.min(parseFloat(formData.homeLoanInterest) || 0, 200000);
    const npsDeduction = Math.min(parseFloat(formData.nps) || 0, 50000);
    
    const totalDeductions = total80C + total80D + homeLoan + npsDeduction;
    taxableIncome = Math.max(0, taxableIncome - totalDeductions);
    
    // Standard deduction for salaried
    taxableIncome = Math.max(0, taxableIncome - 50000);
    
    let tax = 0;
    let exemptionLimit = age === 'above60' ? 300000 : 250000;
    
    if (taxableIncome <= exemptionLimit) {
      tax = 0;
    } else if (taxableIncome <= 500000) {
      tax = (taxableIncome - exemptionLimit) * 0.05;
    } else if (taxableIncome <= 1000000) {
      tax = (500000 - exemptionLimit) * 0.05 + (taxableIncome - 500000) * 0.20;
    } else {
      tax = (500000 - exemptionLimit) * 0.05 + 500000 * 0.20 + (taxableIncome - 1000000) * 0.30;
    }
    
    // Rebate under 87A for old regime (income up to 5L)
    if (taxableIncome <= 500000) {
      tax = 0;
    }
    
    // Health & Education Cess 4%
    const cess = tax * 0.04;
    
    return {
      taxableIncome,
      totalDeductions,
      tax,
      cess,
      totalTax: tax + cess,
    };
  };

  const calculateNewRegimeTax = (income) => {
    let taxableIncome = income;
    
    // Standard deduction for new regime (FY 2024-25)
    taxableIncome = Math.max(0, taxableIncome - 75000);
    
    let tax = 0;
    
    if (taxableIncome <= 300000) {
      tax = 0;
    } else if (taxableIncome <= 600000) {
      tax = (taxableIncome - 300000) * 0.05;
    } else if (taxableIncome <= 900000) {
      tax = 15000 + (taxableIncome - 600000) * 0.10;
    } else if (taxableIncome <= 1200000) {
      tax = 45000 + (taxableIncome - 900000) * 0.15;
    } else if (taxableIncome <= 1500000) {
      tax = 90000 + (taxableIncome - 1200000) * 0.20;
    } else {
      tax = 150000 + (taxableIncome - 1500000) * 0.30;
    }
    
    // Rebate under 87A for new regime (income up to 12L)
    if (taxableIncome <= 1200000) {
      tax = 0;
    }
    
    // Health & Education Cess 4%
    const cess = tax * 0.04;
    
    return {
      taxableIncome,
      totalDeductions: 75000, // Only standard deduction
      tax,
      cess,
      totalTax: tax + cess,
    };
  };

  const handleCalculate = () => {
    const income = parseFloat(formData.totalIncome) || 0;
    
    if (income <= 0) {
      alert('Please enter a valid income amount');
      return;
    }

    const oldResult = calculateOldRegimeTax(income, formData.age);
    const newResult = calculateNewRegimeTax(income);
    
    setResult({
      income,
      old: oldResult,
      new: newResult,
      recommended: newResult.totalTax <= oldResult.totalTax ? 'new' : 'old',
      savings: Math.abs(newResult.totalTax - oldResult.totalTax),
    });
  };

  const handleReset = () => {
    setFormData({
      totalIncome: '',
      age: 'below60',
      taxRegime: 'new',
      deduction80C: '',
      deduction80D: '',
      homeLoanInterest: '',
      nps: '',
    });
    setResult(null);
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <div data-testid="income-tax-calculator-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6">
              <Calculator className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Income Tax Calculator <span className="text-gradient">FY 2024-25</span>
            </h1>
            <p className="text-slate-400">
              Calculate your income tax under both Old and New Tax Regimes. Compare and choose the best option for you.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <div className="bg-navy-800 border border-white/10 rounded-2xl p-6 md:p-8">
              <h2 className="font-heading font-semibold text-xl text-white mb-6">Enter Your Details</h2>
              
              <div className="space-y-5">
                {/* Total Income */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Total Annual Income (₹)</Label>
                  <Input
                    type="number"
                    data-testid="input-income"
                    value={formData.totalIncome}
                    onChange={(e) => setFormData({...formData, totalIncome: e.target.value})}
                    placeholder="Enter your annual income"
                    className="bg-navy-900 border-white/10 text-white text-lg"
                  />
                </div>

                {/* Age Selection */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Age Group</Label>
                  <Select value={formData.age} onValueChange={(v) => setFormData({...formData, age: v})}>
                    <SelectTrigger data-testid="select-age" className="bg-navy-900 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-800 border-white/10">
                      <SelectItem value="below60" className="text-white hover:bg-navy-700">Below 60 Years</SelectItem>
                      <SelectItem value="above60" className="text-white hover:bg-navy-700">60 Years or Above</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Deductions Section (Old Regime) */}
                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <Info className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400 text-sm font-medium">Deductions (for Old Regime comparison)</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">80C (Max ₹1.5L)</Label>
                      <Input
                        type="number"
                        data-testid="input-80c"
                        value={formData.deduction80C}
                        onChange={(e) => setFormData({...formData, deduction80C: e.target.value})}
                        placeholder="0"
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">80D (₹25k/₹50k)</Label>
                      <Input
                        type="number"
                        data-testid="input-80d"
                        value={formData.deduction80D}
                        onChange={(e) => setFormData({...formData, deduction80D: e.target.value})}
                        placeholder="0"
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">Home Loan Interest</Label>
                      <Input
                        type="number"
                        data-testid="input-homeloan"
                        value={formData.homeLoanInterest}
                        onChange={(e) => setFormData({...formData, homeLoanInterest: e.target.value})}
                        placeholder="0"
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">NPS (Max ₹50k)</Label>
                      <Input
                        type="number"
                        data-testid="input-nps"
                        value={formData.nps}
                        onChange={(e) => setFormData({...formData, nps: e.target.value})}
                        placeholder="0"
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-4 pt-4">
                  <Button
                    data-testid="calculate-button"
                    onClick={handleCalculate}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6"
                  >
                    <Calculator className="mr-2 w-5 h-5" />
                    Calculate Tax
                  </Button>
                  <Button
                    data-testid="reset-button"
                    onClick={handleReset}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 py-6"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6">
              {result ? (
                <>
                  {/* Recommendation Banner */}
                  <div className={`rounded-2xl p-6 ${
                    result.recommended === 'new' 
                      ? 'bg-gradient-to-r from-blue-500/20 to-sky-500/20 border border-blue-500/30' 
                      : 'bg-gradient-to-r from-sky-500/20 to-cyan-500/20 border border-sky-500/30'
                  }`}>
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                      <h3 className="font-heading font-semibold text-lg text-white">Recommended Regime</h3>
                    </div>
                    <p className="text-2xl font-heading font-bold text-white mb-1">
                      {result.recommended === 'new' ? 'New Tax Regime' : 'Old Tax Regime'}
                    </p>
                    <p className="text-green-400 text-sm">
                      You save {formatCurrency(result.savings)} with this regime
                    </p>
                  </div>

                  {/* Comparison Cards */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* New Regime */}
                    <div data-testid="new-regime-result" className={`bg-navy-800 border rounded-2xl p-6 ${
                      result.recommended === 'new' ? 'border-blue-500/50' : 'border-white/10'
                    }`}>
                      {result.recommended === 'new' && (
                        <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full mb-3">
                          RECOMMENDED
                        </span>
                      )}
                      <h4 className="font-heading font-semibold text-white mb-4">New Tax Regime</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Gross Income</span>
                          <span className="text-white">{formatCurrency(result.income)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Standard Deduction</span>
                          <span className="text-white">₹75,000</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Taxable Income</span>
                          <span className="text-white">{formatCurrency(result.new.taxableIncome)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Income Tax</span>
                          <span className="text-white">{formatCurrency(result.new.tax)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Cess (4%)</span>
                          <span className="text-white">{formatCurrency(result.new.cess)}</span>
                        </div>
                        <div className="border-t border-white/10 pt-3 flex justify-between">
                          <span className="text-white font-medium">Total Tax</span>
                          <span className="text-blue-400 font-bold text-lg">{formatCurrency(result.new.totalTax)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Old Regime */}
                    <div data-testid="old-regime-result" className={`bg-navy-800 border rounded-2xl p-6 ${
                      result.recommended === 'old' ? 'border-sky-500/50' : 'border-white/10'
                    }`}>
                      {result.recommended === 'old' && (
                        <span className="inline-block px-3 py-1 bg-sky-500/20 text-sky-400 text-xs font-medium rounded-full mb-3">
                          RECOMMENDED
                        </span>
                      )}
                      <h4 className="font-heading font-semibold text-white mb-4">Old Tax Regime</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Gross Income</span>
                          <span className="text-white">{formatCurrency(result.income)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Total Deductions</span>
                          <span className="text-green-400">-{formatCurrency(result.old.totalDeductions + 50000)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Taxable Income</span>
                          <span className="text-white">{formatCurrency(result.old.taxableIncome)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Income Tax</span>
                          <span className="text-white">{formatCurrency(result.old.tax)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Cess (4%)</span>
                          <span className="text-white">{formatCurrency(result.old.cess)}</span>
                        </div>
                        <div className="border-t border-white/10 pt-3 flex justify-between">
                          <span className="text-white font-medium">Total Tax</span>
                          <span className="text-sky-400 font-bold text-lg">{formatCurrency(result.old.totalTax)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Placeholder when no result */
                <div className="bg-navy-800 border border-white/10 rounded-2xl p-8 flex flex-col items-center justify-center min-h-[400px]">
                  <Calculator className="w-16 h-16 text-slate-600 mb-4" />
                  <h3 className="font-heading font-semibold text-xl text-white mb-2">Enter Your Details</h3>
                  <p className="text-slate-400 text-center max-w-xs">
                    Fill in your income and deductions to see a comparison between Old and New Tax Regimes
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Tax Slabs Reference */}
      <section className="py-12 md:py-16 bg-navy-800/50">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-heading font-bold text-2xl text-white text-center mb-8">
            Tax Slab Rates FY 2024-25
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-navy-800 border border-white/5 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-lg text-blue-400 mb-4">New Tax Regime</h3>
              <div className="space-y-2 text-sm">
                {[
                  { range: '₹0 - ₹3L', rate: 'Nil' },
                  { range: '₹3L - ₹6L', rate: '5%' },
                  { range: '₹6L - ₹9L', rate: '10%' },
                  { range: '₹9L - ₹12L', rate: '15%' },
                  { range: '₹12L - ₹15L', rate: '20%' },
                  { range: 'Above ₹15L', rate: '30%' },
                ].map((s, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-white/5">
                    <span className="text-slate-400">{s.range}</span>
                    <span className="text-white">{s.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-green-400 text-xs mt-3">New Tax Regime Rebate: Income upto ₹12 Lac = No Tax</p>
            </div>
            <div className="bg-navy-800 border border-white/5 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-lg text-sky-400 mb-4">Old Tax Regime</h3>
              <div className="space-y-2 text-sm">
                {[
                  { range: '₹0 - ₹2.5L', rate: 'Nil' },
                  { range: '₹2.5L - ₹5L', rate: '5%' },
                  { range: '₹5L - ₹10L', rate: '20%' },
                  { range: 'Above ₹10L', rate: '30%' },
                ].map((s, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-white/5">
                    <span className="text-slate-400">{s.range}</span>
                    <span className="text-white">{s.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-green-400 text-xs mt-3">Rebate: No tax if income ≤ ₹5L</p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-navy-800/50 border border-white/5 rounded-xl p-4">
            <p className="text-slate-500 text-xs text-center">
              <strong>Disclaimer:</strong> This calculator provides an estimate based on FY 2024-25 tax rates. 
              Actual tax liability may vary. Please consult a tax professional for accurate calculations and advice.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
